import { client } from './client';
import type {
  AppointmentPayment,
  SessionPayments,
  AuditResponse,
  Branch,
  CalendarResponse,
  CategoryNode,
  CreateSessionBody,
  Customer,
  Dashboard,
  Report,
  ReportGroupBy,
  Resource,
  Service,
  Session,
  Status,
  Tag,
  UpdateSessionBody,
} from './types';

const http = client.axios;
const P = '/api/v1/appointments';

interface ListEnvelope<T> { data: T[] }

/** One tender row, the same shape the cashier's payment modal posts. */
export interface ConvertPayment {
  type_code: number;
  amount: number;
}

export interface ConvertBody {
  /** Splits from the payment dialog. Σ amounts must equal the sale total. */
  payments?: ConvertPayment[];
  /** Legacy single-method shortcut, used when `payments` is absent. */
  payment_type_code?: number;
  /** Cash handed over when it exceeds the total; the difference is change. */
  cash_tendered?: number;
  tax_percentage?: number;
  complete?: boolean;
}

/**
 * What a screen needs once the sale exists, whichever way it was paid.
 *
 * The cash path returns the whole conversion envelope and the card path only
 * learns the order back from the Tap poller, so the callback both dialogs
 * share is typed on the intersection rather than on the richer of the two.
 */
export interface PaidSummary {
  order_id: number;
  entry_no: number;
  net_value: number;
  change: number;
  deposit_applied: number;
}

export interface ConvertResult extends PaidSummary {
  gl_receipt_code: number;
  idempotent_replay: boolean;
  session: Session;
}

/**
 * Open a Tap charge for the balance of an appointment.
 *
 * The reply is only the hosted payment page. The receipt is written by the
 * ORDINARY Tap poller (`tapPaymentAPI.pollCharge`) on capture, which also
 * stamps the appointment — see `openConversionTapCharge` on the server for why
 * this app does not finalise the sale itself.
 */
export interface TapChargeBody {
  /** Which hosted gateway takes the balance; Tap when omitted. */
  gateway?: 'tap' | 'upayments' | 'tabby' | 'tamara' | 'myfatoorah' | 'knet';
  tax_percentage?: number;
  complete?: boolean;
}

export interface TapChargeResult {
  gateway: 'tap' | 'upayments' | 'tabby' | 'tamara' | 'myfatoorah' | 'knet';
  charge_id: string;
  redirect_url: string;
  terminal_mode: 'qr' | 'iframe';
  /** What the card is charged: the invoice less any deposit. */
  amount: number;
  currency: string;
  total: number;
  deposit_applied: number;
}

export const api = {
  calendar: (branchId: number, params: { date?: string; from?: string; to?: string; employeeId?: number }) =>
    http
      .get<CalendarResponse>(`${P}/calendar`, {
        params: {
          branch_id: branchId,
          ...(params.date ? { date: params.date } : {}),
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
          ...(params.employeeId ? { employee_id: params.employeeId } : {}),
        },
      })
      .then((r) => r.data),

  dashboard: (branchId: number, params: { from?: string; to?: string }) =>
    http
      .get<Dashboard>(`${P}/dashboard`, {
        params: { branch_id: branchId, ...(params.from ? { from: params.from } : {}), ...(params.to ? { to: params.to } : {}) },
      })
      .then((r) => r.data),

  report: (branchId: number, params: { group_by: ReportGroupBy; from?: string; to?: string; employee_id?: number; status_id?: number }) =>
    http
      .get<Report>(`${P}/reports`, {
        params: {
          branch_id: branchId, group_by: params.group_by,
          ...(params.from ? { from: params.from } : {}), ...(params.to ? { to: params.to } : {}),
          ...(params.employee_id ? { employee_id: params.employee_id } : {}),
          ...(params.status_id ? { status_id: params.status_id } : {}),
        },
      })
      .then((r) => r.data),

  audit: (params: { sessionId?: number; entityType?: string; action?: string; from?: string; to?: string; page?: number; limit?: number }) =>
    http
      .get<AuditResponse>(`${P}/audit`, {
        params: {
          ...(params.sessionId ? { session_id: params.sessionId } : {}),
          ...(params.entityType ? { entity_type: params.entityType } : {}),
          ...(params.action ? { action: params.action } : {}),
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
          page: params.page ?? 1,
          limit: params.limit ?? 50,
        },
      })
      .then((r) => r.data),

  branches: () => http.get<ListEnvelope<Branch>>(`${P}/branches`).then((r) => r.data.data),
  resources: (branchId: number) =>
    http.get<ListEnvelope<Resource>>(`${P}/resources`, { params: { branch_id: branchId } }).then((r) => r.data.data),
  statuses: () => http.get<ListEnvelope<Status>>(`${P}/statuses`).then((r) => r.data.data),
  tags: () => http.get<ListEnvelope<Tag>>(`${P}/tags`).then((r) => r.data.data),

  categories: (branchId: number) =>
    http.get<ListEnvelope<CategoryNode>>(`${P}/categories`, { params: { branch_id: branchId } }).then((r) => r.data.data),
  services: (params: { categoryId?: number; q?: string }) =>
    http
      .get<ListEnvelope<Service>>(`${P}/services`, {
        params: { ...(params.categoryId ? { category_id: params.categoryId } : {}), ...(params.q ? { q: params.q } : {}) },
      })
      .then((r) => r.data.data),

  searchCustomers: (q: string) =>
    http.get<ListEnvelope<Customer>>(`${P}/customers`, { params: { q } }).then((r) => r.data.data),
  createCustomer: (body: { name: string; name_ar?: string; phone: string; email?: string }) =>
    http.post<Customer>(`${P}/customers`, body).then((r) => r.data),

  getSession: (id: number) => http.get<Session>(`${P}/sessions/${id}`).then((r) => r.data),
  createSession: (body: CreateSessionBody) => http.post<Session>(`${P}/sessions`, body).then((r) => r.data),
  updateSession: (id: number, body: UpdateSessionBody) => http.put<Session>(`${P}/sessions/${id}`, body).then((r) => r.data),
  changeStatus: (id: number, body: { status_id?: number; status_code?: string; reason?: string }) =>
    http.patch<Session>(`${P}/sessions/${id}/status`, body).then((r) => r.data),
  convert: (id: number, body: ConvertBody) =>
    http.post<ConvertResult>(`${P}/sessions/${id}/convert`, body).then((r) => r.data),
  tapCharge: (id: number, body: TapChargeBody) =>
    http.post<TapChargeResult>(`${P}/sessions/${id}/tap-charge`, body).then((r) => r.data),
  deleteSession: (id: number) => http.delete(`${P}/sessions/${id}`).then(() => undefined),

  // ── Reception-collected deposits (migration 058) ──────────────────────────
  /** `poll` asks Tap about pending links first — the open screen's refresh. */
  payments: (id: number, opts: { poll?: boolean } = {}) =>
    http
      .get<SessionPayments | null>(`${P}/sessions/${id}/payments`, { params: opts.poll ? { poll: 1 } : {} })
      .then((r) => r.data),
  createPayment: (id: number, body: CreatePaymentBody) =>
    http.post<CreatePaymentResult>(`${P}/sessions/${id}/payments`, body).then((r) => r.data),
  remindPayment: (id: number, paymentId: number) =>
    http.post<RemindPaymentResult>(`${P}/sessions/${id}/payments/${paymentId}/remind`, {}).then((r) => r.data),
  cancelPayment: (id: number, paymentId: number) =>
    http.post<SessionPayments>(`${P}/sessions/${id}/payments/${paymentId}/cancel`, {}).then((r) => r.data),
};

export interface CreatePaymentBody {
  amount: number;
  payment_type_code: number;
  method: 'tap_invoice' | 'manual';
  /** Mobile the Tap link goes to; defaults to the appointment's customer phone. */
  phone?: string;
  /** When given, Tap also e-mails the link. */
  email?: string;
  note?: string;
}

export type LinkSentVia = 'tap_sms' | 'tap_sms_email' | 'pos_sms' | 'none';

export interface CreatePaymentResult {
  payment: AppointmentPayment;
  summary: SessionPayments;
  /** Null for a manual tender; how the customer was told about a Tap link otherwise. */
  link_sent_via: LinkSentVia | null;
}

export interface RemindPaymentResult {
  sent_via: 'tap_sms' | 'pos_sms' | 'none';
  summary: SessionPayments;
}
